import { esimService } from "@/lib/api/services/esim";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Esim,
  EsimPlan,
  TopUpOptionsResponse,
  Country,
  Region,
  PublicTopUpOptionsResponse,
  SinglePublicPlan,
  ComparisonData,
  PublicAllPlansResponse
} from "@/types/esim";
import { logger } from "@/lib/logger";

export function useEsimQuery() {
  return useQuery<Esim[]>({
    queryKey: ["customer-esims"],
    queryFn: async () => {
      try {
        return await esimService.getEsim();
      } catch (error) {
        logger.error("eSim query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function useCountries() {
  return useQuery<Country[]>({
    queryKey: ["esim-countries"],
    queryFn: async () => {
      try {
        return await esimService.getCountries();
      } catch (error) {
        logger.error("eSim countries query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function useRegions() {
  return useQuery<Region[]>({
    queryKey: ["esim-regions"],
    queryFn: async () => {
      try {
        return await esimService.getRegion();
      } catch (error) {
        logger.error("eSim regions query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function useEsimPlansQuery(iccid: string) {
  return useQuery<EsimPlan[]>({
    queryKey: ["esim-plans", iccid],
    queryFn: async () => {
      try {
        return await esimService.getEsimByIccid(iccid);
      } catch (error) {
        logger.error("eSim plan query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function useEsimTopUpOptions(
  keyword: string,
  page: number,
  isCountry: boolean,
  filters: {
    dataGb: string;
    validity: string;
    planType: string;
  },
) {
  return useQuery<TopUpOptionsResponse>({
    queryKey: [
      "esim-top-up",
      keyword,
      page,
      isCountry,
      filters.dataGb,
      filters.validity,
      filters.planType,
    ],
    queryFn: async () => {
      try {
        return await esimService.getTopUpOptions(keyword, page, isCountry, 6, filters);
      } catch (error) {
        logger.error("eSim top up options query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
    enabled: !!keyword,
  });
}

export function useEsimTopUpMutation() {
  return useMutation({
    mutationKey: ["top-up"],
    mutationFn: async (payload: { iccid: string; planSlug: string; loyalty_points?: number }) => {
      try {
        return await esimService.topUpEsim(payload);
      } catch (error) {
        logger.error("Esim top-up service error in mutation", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });
}

export function useEsimAutoTopUp(){
  return useMutation(
    {
      mutationKey: ["auto-top-up"],
      mutationFn: async ( payload: {id: string, auto_topup_enabled: number}) =>{
        try{
          return await esimService.autoTopUpEsim(payload)
        }
        catch(error){
          logger.error("Esim top-up service error in mutation", {
          error: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
      }
    },
  )
}

export function useSsimAutoTopUpQuery(id: string){
  return useQuery({
    queryKey: ["auto-top-up-esim-plans-history", id],
    queryFn: async () => {
      try {
        return await esimService.getAutoTopUpHistory(id);
      } catch (error) {
        logger.error("eSim plan query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function useEsimCreateMutation() {
  return useMutation({
    mutationKey: ["create-top-up"],
    mutationFn: async (payload: { plan_id: number; loyalty_points?: number }) => {
      try {
        return await esimService.createEsim(payload);
      } catch (error) {
        logger.error("Esim top-up service error in mutation", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });
}

export function useEsimCreditCardMutation() {
  return useMutation({
    mutationKey: ["credit-card-esim-creation"],
    mutationFn: async (payload: { plan_id: number; loyalty_points?: number }) => {
      try {
        return await esimService.createEsimWithCreditCard(payload);
      } catch (error) {
        logger.error("Credit card eSim creation service error in mutation", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });
}

export function useEsimTopUpCreditCardMutation() {
  return useMutation({
    mutationKey: ["credit-card-top-up"],
    mutationFn: async (payload: { iccid: string; planSlug: string; loyalty_points?: number }) => {
      try {
        return await esimService.esimTopUpWithCreditCard(payload);
      } catch (error) {
        logger.error("Credit card eSim creation service error in mutation", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });
}

export function usePlanBySlug(slug: string) {
  return useQuery<SinglePublicPlan>({
    queryKey: ["single-esim-plan", slug],
    queryFn: async () => {
      try {
        return await esimService.getPlanBySlug(slug);
      } catch (error) {
        logger.error("Single eSim plan query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

// public hooks

export function usePopularEsimPlans() {
  return useQuery({
    queryKey: ["travel-esim-popular"],
    queryFn: async () => {
      try {
        const response = await esimService.getPopularEsimPlans();
        return response;
      } catch (error) {
        logger.error("Failed to fetch popular eSim plans", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function usePublicCountries() {
  return useQuery<Country[]>({
    queryKey: ["travel-esim-countries"],
    queryFn: async () => {
      try {
        return await esimService.getPublicCountries();
      } catch (error) {
        logger.error("eSim countries query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function usePublicRegions() {
  return useQuery<{ status: string; message: string; data: Region[] }>({
    queryKey: ["travel-esim-regions"],
    queryFn: async () => {
      try {
        return await esimService.getPublicRegions();
      } catch (error) {
        logger.error("eSim regions query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function usePublicRegionPlans(
  region_code: string,
  page: number,
  filters: {
    dataGb: string;
    validity: string;
    planType: string;
  },
) {
  return useQuery<PublicTopUpOptionsResponse>({
    queryKey: [
      "travel-esim-region-plans",
      region_code,
      page,
      filters.dataGb,
      filters.validity,
      filters.planType,
    ],
    queryFn: async () => {
      try {
        return await esimService.getPublicPlansByRegion(region_code, page, 12, filters);
      } catch (error) {
        logger.error("eSim region plans query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function usePublicCountryPlans(
  keyword: string,
  page: number,
  filters: {
    dataGb: string;
    validity: string;
    planType: string;
  },
) {
  return useQuery<PublicTopUpOptionsResponse>({
    queryKey: [
      "travel-esim-country-plans",
      keyword,
      page,
      filters.dataGb,
      filters.validity,
      filters.planType,
    ],
    queryFn: async () => {
      try {
        return await esimService.getPublicPlansByCountry(keyword, page, 12, filters);
      } catch (error) {
        logger.error("eSim country plans query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function usePublicPlanBySlug(slug: string) {
  return useQuery<SinglePublicPlan>({
    queryKey: ["travel-esim-plan", slug],
    queryFn: async () => {
      try {
        return await esimService.getPublicPlanBySlug(slug);
      } catch (error) {
        logger.error("eSim plan query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    staleTime: 60_000,
    enabled: !!slug,
  });
}

export function useEsimPublicCreateEsimMutation() {
  return useMutation({
    mutationKey: ["public-top-up"],
    mutationFn: async (payload: { plan_id: string; email: string }) => {
      try {
        return await esimService.createPublicEsim(payload);
      } catch (error) {
        logger.error("Public eSim creation service error in mutation", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });
}

export function useEsimComparisonQuery(scope: string, currency?: string, is_home_page?: boolean) {
  return useQuery<ComparisonData>({
    queryKey: ["travel-esim-comparison", scope, currency],
    queryFn: async () => {
      try {
        return await esimService.getCompetitorComparison(scope, currency, is_home_page);
      } catch (error) {
        logger.error("eSim comparison query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
  });
}

export function useEsimCompareQuery(scope: string, currency?: string) {
  return useQuery<ComparisonData>({
    queryKey: ["travel-esim-comparison-page", scope, currency],
    queryFn: async () => {
      try {
        return await esimService.getCompetitorComparison(scope, currency);
      } catch (error) {
        logger.error("eSim comparison query failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    enabled: !!scope,
  });
}


export function useGetEsimByKeyWord(
  countryOrRegionName: string,
  page: number = 1,
  filters: {
    dataGb: string;
    validity: string;
    planType: string;
    sort: "asc" | "desc";
  } = { dataGb: "all", validity: "all", planType: "all", sort: "asc" },
) {
  return useQuery<PublicAllPlansResponse>({
    queryKey: ["all-public-eSIM-plans", countryOrRegionName, page, filters.dataGb, filters.validity, filters.planType, filters.sort],
    queryFn: async () => {
      try {
        return await esimService.getAllPublicPlans(countryOrRegionName, page, 8, filters);
      } catch (error) {
        logger.error("get eSIM plan by countryOrRegionName failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    enabled: !!countryOrRegionName,
  });
}